/**
 * @file ChatInput.tsx
 * @description Componente unificado para entrada de chat que elimina duplicación
 * 
 * Este componente unifica las dos interfaces duplicadas en Chat.jsx:
 * 1. Primera interfaz (líneas ~1020-1080) - Estado inicial
 * 2. Segunda interfaz (líneas ~1420-1520) - Chat con mensajes
 * 
 * IMPORTANTE: Mantiene compatibilidad total con props actuales
 */

import React, { useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  useTheme,
  Tooltip,
  Alert,
  alpha
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MicIcon from '@mui/icons-material/Mic';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useTranslation } from 'react-i18next';
import { ChatFileUploader } from './upload/ChatFileUploader';
import { FilePreviewList, type UploadingBatch } from './FilePreviewList';
import { generateClientThumbnailsBatch, type FileWithThumbnail } from '../../utils/thumbnailGenerator';
import ChatConfigMenu from './ChatConfigMenu';
import featureFlags from '../../config/featureFlags';

// ✅ Expose ref interface for parent components
export interface ChatInputHandle {
  focus: () => void;
}

// Maximum character limit for user messages (similar to ChatGPT's ~4,000, but more generous)
const MAX_MESSAGE_LENGTH = 10000;

const { SHOW_VOICE_INPUT } = featureFlags;

interface ChatInputProps {
  /**
   * Callback que se llama al enviar el mensaje. Recibe el texto del mensaje y los archivos con thumbnails.
   */
  onSend: (message: string, filesWithThumbnails: FileWithThumbnail[]) => void;
  disabled?: boolean;
  placeholder?: string;

  // Props para archivos
  filesWithThumbnails: FileWithThumbnail[]; // ✅ CHANGED: Receive files with thumbnails directly
  onFilesChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (file: File) => void;
  onValidationError?: (error: string | null) => void;
  
  // Props para audio
  isRecording: boolean;
  isProcessingAudio: boolean;
  onStartRecording: () => void;
  onStopRecording: () => void;
  
  // Props para estado del agente
  isAgentCurrentlyActive?: boolean;
  
  // Props para stop
  onStop?: () => void;
  isStoppingExecution?: boolean;
  
  // Props para errores
  audioError?: string | null;
  permissionNeeded?: boolean;
  
  // Props para validación
  fileValidationError?: string | null;
  contextValidationError?: string | null;
  fileUploadError?: string | null;
  isChatGloballyDisabled?: boolean;

  // Props para borrador por hilo
  threadId?: string;
  savedDraft?: string;
  onDraftChange?: (text: string) => void;
  /**
   * El plan está agotado: enviar no va a funcionar porque el backend lo rechaza
   * con un 402. El botón NO se quita ni se deshabilita de forma nativa — un
   * `disabled` real se traga el `click` y el usuario no se enteraría de por qué
   * no pasa nada. Se pinta apagado, se marca `aria-disabled` y al pulsarlo se
   * abre el aviso (#1300).
   */
  creditBlocked?: boolean;
  /** Qué hacer cuando se intenta enviar sin crédito: abrir el aviso. */
  onCreditBlocked?: () => void;
  /**
   * Cada incremento repone `savedDraft` en el input SIN cambiar de hilo. Lo usa
   * el padre cuando un envío falla —quedarse sin crédito, por ejemplo—: el texto
   * se limpia al pulsar enviar y hasta ahora se perdía con el mensaje (#1299).
   * El efecto de `savedDraft` sólo reacciona a `threadId`, de ahí el contador.
   */
  restoreNonce?: number;

  // Upload progress UI (Fase 1)
  uploadingBatch?: UploadingBatch | null;
  onCancelUpload?: () => void;
  onDismissUploadError?: () => void;
}

const ChatInputInner = forwardRef<ChatInputHandle, ChatInputProps>(({
  onSend,
  disabled = false,
  placeholder,
  filesWithThumbnails, // ✅ CHANGED: Receive filesWithThumbnails from parent
  onFilesChange,
  onRemoveFile,
  onValidationError,
  isRecording,
  isProcessingAudio,
  onStartRecording,
  onStopRecording,
  isAgentCurrentlyActive = false,
  onStop,
  isStoppingExecution = false,
  audioError,
  permissionNeeded,
  fileValidationError,
  contextValidationError,
  fileUploadError,
  isChatGloballyDisabled = false,
  threadId,
  savedDraft,
  onDraftChange,
  creditBlocked = false,
  onCreditBlocked,
  restoreNonce = 0,
  uploadingBatch,
  onCancelUpload,
  onDismissUploadError,
}, ref) => {
  const theme = useTheme();
  const { t } = useTranslation();

  // Estado local del input
  const [inputValue, setInputValue] = useState('');
  const [messageLengthError, setMessageLengthError] = useState<string | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  // Ref para auto-focus
  const inputRef = useRef<HTMLInputElement>(null);

  // ── Debounce draft changes to avoid parent re-renders on every keystroke ──
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingDraftRef = useRef<string | null>(null);

  const debouncedDraftChange = useCallback((text: string) => {
    pendingDraftRef.current = text;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      onDraftChange?.(text);
      pendingDraftRef.current = null;
    }, 300);
  }, [onDraftChange]);

  // Flush pending draft on unmount to prevent data loss on navigation
  useEffect(() => {
    return () => {
      if (draftTimerRef.current) {
        clearTimeout(draftTimerRef.current);
      }
      if (pendingDraftRef.current !== null && onDraftChange) {
        onDraftChange(pendingDraftRef.current);
      }
    };
  }, [onDraftChange]);

  // ✅ Expose focus method to parent via ref
  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    }
  }));

  // Auto-focus en el input cuando el componente se monta
  useEffect(() => {
    // Usar setTimeout para asegurar que el DOM esté completamente renderizado
    const timer = setTimeout(() => {
      if (inputRef.current && !isInputDisabled) {
        inputRef.current.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []); // Solo ejecutar al montar el componente

  // Restaurar borrador al cambiar de hilo
  useEffect(() => {
    setInputValue(savedDraft ?? '');
    setMessageLengthError(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]); // Intencional: sólo reacciona a cambios de threadId

  // `savedDraft` se lee por ref para que reponer NO dependa de tenerlo en las
  // deps del efecto: si estuviera, el input se reescribiría en cada pulsación.
  const savedDraftRef = useRef(savedDraft);
  savedDraftRef.current = savedDraft;

  // Reponer el borrador tras un envío fallido, sin cambiar de hilo.
  useEffect(() => {
    if (!restoreNonce) return; // 0 = nunca se ha pedido reponer
    setInputValue(savedDraftRef.current ?? '');
    setMessageLengthError(null);
  }, [restoreNonce]);

  // ✅ REMOVED: Thumbnail generation logic (now handled by useFileUploadWithThumbnails hook in parent)

  // Check if message exceeds length limit
  const isMessageTooLong = inputValue.length > MAX_MESSAGE_LENGTH;
  const isMessageEmpty = !inputValue.trim() && filesWithThumbnails.length === 0;

  // Lógica de deshabilitación refinada
  // CAMBIO UX: Permitir escribir durante procesamiento, solo bloquear envío
  const isInputDisabled = isRecording || isProcessingAudio; // Usuario puede escribir mientras agente procesa
  const isSendDisabled = isChatGloballyDisabled || isRecording || isProcessingAudio || isAgentCurrentlyActive || isMessageTooLong || isMessageEmpty; // Bloquear envío durante procesamiento (defense-in-depth)
  const isAudioButtonDisabled = isChatGloballyDisabled || isProcessingAudio || isRecording;
  const isStopButtonDisabled = isChatGloballyDisabled || isProcessingAudio;

  // Determinar placeholder dinámico
  const getPlaceholder = () => {
    if (isRecording) return "Grabando audio...";
    if (isProcessingAudio) return "Procesando audio...";
    return placeholder || t('inputPlaceholder');
  };

  // Determinar si mostrar botón de stop
  const shouldShowStopButton = isAgentCurrentlyActive && !isRecording && !isProcessingAudio;

  // Manejar click en stop
  const handleStopClick = () => {
    if (onStop && !isStoppingExecution) {
      onStop();
    }
  };

  // Manejar envío de mensaje
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Va PRIMERO, antes de validar longitud o contenido: así el aviso sale
    // igual con el Enter que con el botón, y también con la caja vacía. Si
    // estuviera después, un intento de enviar sin texto no explicaría nada.
    if (creditBlocked) {
      onCreditBlocked?.();
      return;
    }

    // Clear previous error
    setMessageLengthError(null);

    // Validate message length
    if (inputValue.length > MAX_MESSAGE_LENGTH) {
      setMessageLengthError(`El mensaje no puede exceder ${MAX_MESSAGE_LENGTH.toLocaleString()} caracteres. Caracteres actuales: ${inputValue.length.toLocaleString()}`);
      return;
    }

    // Validate not empty (allow empty text if files are attached)
    if (!inputValue.trim() && filesWithThumbnails.length === 0) {
      console.log('[ChatInput] Blocked: no text and no files');
      return;
    }
    console.log('[ChatInput] Sending:', { text: inputValue.trim(), filesCount: filesWithThumbnails.length });

    // Send message if all validations pass
    if (!isSendDisabled) {
      // Cancel any pending debounced draft update before clearing
      if (draftTimerRef.current) {
        clearTimeout(draftTimerRef.current);
        draftTimerRef.current = null;
      }
      pendingDraftRef.current = null;

      onSend(inputValue, filesWithThumbnails); // ✅ CHANGED: Send filesWithThumbnails
      setInputValue(''); // Limpiar input tras enviar
      onDraftChange?.(''); // Limpiar borrador del padre tras envío
      setMessageLengthError(null); // Clear error after successful send
    }
  };

  // Manejar tecla Enter
  // Enter = enviar, Shift+Enter = nueva línea (convención estándar: ChatGPT, Claude, Slack)
  // Esc = abortar subida en curso (si la hay)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
      return;
    }
    if (e.key === 'Escape' && uploadingBatch?.status === 'uploading' && onCancelUpload) {
      e.preventDefault();
      onCancelUpload();
    }
  };

  /**
   * Handle paste events to extract files from clipboard
   * Supports pasting images and files (like Claude Web)
   */
  const handlePaste = async (e: React.ClipboardEvent<HTMLDivElement>) => {
    // First try clipboardData.files (works for multiple files from file explorer)
    const clipboardFiles = e.clipboardData.files;
    let newFiles: File[] = [];

    if (clipboardFiles && clipboardFiles.length > 0) {
      // Files pasted from file explorer (Ctrl+C multiple files)
      // Note: Browsers typically only support pasting single files from clipboard
      newFiles = Array.from(clipboardFiles);
    } else {
      // Fallback to items (works for single images from web)
      const items = e.clipboardData.items;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            newFiles.push(file);
          }
        }
      }
    }

    // If no files found, allow default paste behavior (text)
    if (newFiles.length === 0) return;

    // Prevent default paste (we're handling files, not text)
    e.preventDefault();

    // Combine existing files with new files
    const existingFiles = filesWithThumbnails.map(item => item.file);
    const allFiles = [...existingFiles, ...newFiles];

    // Convert to synthetic event and reuse existing validation flow
    const syntheticEvent = {
      target: { files: allFiles }
    } as unknown as React.ChangeEvent<HTMLInputElement>;

    // El hook del padre (configurado con `chatFileOptions` en Chat.jsx) aplica: tipo, tamaño por
    // tipo, y desde #266 también el LOTE — número de ficheros y tamaño agregado. Por eso se le
    // pasa la lista combinada (existentes + nuevos): lo que se valida es el acumulado.
    onFilesChange(syntheticEvent);
  };

  /**
   * Handle drag over to show visual feedback
   */
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';

    if (!isInputDisabled) {
      setIsDragActive(true);
    }
  };

  /**
   * Handle drag leave to clear visual feedback
   */
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  /**
   * Handle drop to extract files
   */
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (isInputDisabled) return;

    const newFiles = Array.from(e.dataTransfer.files);
    if (newFiles.length === 0) return;

    console.log('[ChatInput] Dropped files:', newFiles.length);

    // Combine existing files with new files
    const existingFiles = filesWithThumbnails.map(item => item.file);
    const allFiles = [...existingFiles, ...newFiles];

    // Convert to synthetic event and reuse existing validation flow
    const syntheticEvent = {
      target: { files: allFiles }
    } as unknown as React.ChangeEvent<HTMLInputElement>;

    // Mismo camino que arrastrar: el hook del padre valida tipo, tamaño por tipo y —desde #266—
    // el lote completo (número de ficheros y tamaño agregado) sobre la lista combinada.
    onFilesChange(syntheticEvent);
  };

  // ✅ Wrapper para convertir File[] a evento para el hook
  const handleFilesChangeWrapper = (files: File[]) => {
    // ChatFileUploader pasa File[], pero el hook espera ChangeEvent
    // Creamos un evento sintético con conversión forzada
    const syntheticEvent = {
      target: {
        files: files
      }
    } as unknown as React.ChangeEvent<HTMLInputElement>;

    onFilesChange(syntheticEvent);
  };

  // ── Memoize heavy MUI sx object to avoid recreation on every render ──
  const textFieldSx = useMemo(() => ({
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      transition: 'height 0.1s ease-out',
      py: 0,
      minHeight: { xs: 44, md: 48 },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.divider,
      },
      ...(isDragActive && {
        borderColor: 'primary.main',
        borderWidth: 2,
        borderStyle: 'dashed',
        backgroundColor: (t: any) => alpha(t.palette.primary.main, 0.05)
      }),
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: alpha(theme.palette.primary.main, 0.6),
      },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.divider,
        }
      }
    },
    '& .MuiOutlinedInput-input': {
      py: { xs: 0.75, md: 1 },
      fontSize: { xs: '0.95rem', md: '1rem' },
      lineHeight: 1.4,
      resize: 'none',
      wordBreak: 'break-word',
      overflowWrap: 'break-word'
    }
  }), [theme.palette.divider, theme.palette.primary.main, theme.palette.action.disabledBackground, isDragActive]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        px: { xs: 1.5, md: 2 },  // 12px horizontal padding on mobile, 16px on desktop
        py: { xs: 1.5, md: 2 },  // Vertical padding slightly reduced on mobile
        // iOS safe area insets for home indicator and browser chrome
        paddingBottom: {
          xs: 'max(12px, env(safe-area-inset-bottom))',
          md: '16px'
        },
        position: 'relative',
        zIndex: 900
      }}
    >
      {/* Alertas de error */}
      {audioError && (
        <Box sx={{ 
          position: 'absolute', 
          top: '-50px', 
          left: '1rem', 
          right: '1rem',
          zIndex: 1000
        }}>
          {/* Aquí iría el Alert component si fuera necesario */}
        </Box>
      )}
      
      {permissionNeeded && (
        <Box sx={{ 
          position: 'absolute', 
          top: '-50px', 
          left: '1rem', 
          right: '1rem',
          zIndex: 1000
        }}>
          {/* Aquí iría el Alert component si fuera necesario */}
        </Box>
      )}

      {/* Área para mostrar archivos seleccionados con thumbnails (o batch en subida) */}
      {(filesWithThumbnails.length > 0 || uploadingBatch) && (
        <FilePreviewList
          filesWithThumbnails={filesWithThumbnails}
          onRemoveFile={onRemoveFile}
          uploadingBatch={uploadingBatch ?? null}
          onCancelUpload={onCancelUpload}
          onDismissUploadError={onDismissUploadError}
        />
      )}

      {/* Error de longitud de mensaje */}
      {messageLengthError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMessageLengthError(null)}>
          {messageLengthError}
        </Alert>
      )}

      {/* Layout vertical: Input arriba, botones debajo (estilo Claude) */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1.5, md: 1 }
      }}>
        {/* ROW 1: Input de texto (ocupa 100% width) */}
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{ width: '100%' }}
        >
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={8}
            variant="outlined"
            placeholder={getPlaceholder()}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              debouncedDraftChange(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            disabled={isInputDisabled}
            autoComplete="off"
            inputRef={inputRef}
            inputProps={{ inputMode: 'text', enterKeyHint: 'send' }}
            sx={textFieldSx}
          />
        </Box>

        {/* ROW 2: Botones organizados en grupos (izquierda y derecha) */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1
        }}>
          {/* Grupo izquierdo: Adjuntar y Config */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ChatFileUploader
              onFilesChange={handleFilesChangeWrapper}
              disabled={isInputDisabled}
              currentMessage={inputValue}
              onValidationError={onValidationError}
              selectedFiles={filesWithThumbnails.map(item => item.file)}
            />
            <ChatConfigMenu disabled={isInputDisabled} />
          </Box>

          {/* Grupo derecho: Audio, Send/Stop, Spinner */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Botón de audio (micrófono/stop grabación) */}
            {SHOW_VOICE_INPUT && (
              <IconButton
                sx={{
                  width: { xs: 32, md: 40 },
                  height: { xs: 32, md: 40 },
                  backgroundColor: { xs: 'transparent', md: 'auto' }
                }}
                color={isRecording ? "error" : "primary"}
                onClick={isRecording ? onStopRecording : onStartRecording}
                disabled={isRecording ? isStopButtonDisabled : isAudioButtonDisabled}
              >
                {isRecording ? <StopCircleIcon sx={{ fontSize: { xs: 20, md: 24 } }} /> : <MicIcon sx={{ fontSize: { xs: 20, md: 24 } }} />}
              </IconButton>
            )}

            {/* Botón de Stop o Envío */}
            {shouldShowStopButton ? (
              <IconButton
                onClick={handleStopClick}
                disabled={isStoppingExecution}
                sx={{
                  width: { xs: 32, md: 40 },
                  height: { xs: 32, md: 40 },
                  backgroundColor: alpha(theme.palette.error.light, 0.8),
                  color: theme.palette.error.contrastText,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.error.light, 0.67),
                    cursor: 'pointer',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: alpha(theme.palette.error.light, 0.27),
                    color: theme.palette.error.contrastText
                  }
                }}
              >
                {isStoppingExecution ? (
                  <CircularProgress size={24} sx={{ color: 'inherit' }} />
                ) : (
                  <StopCircleIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
                )}
              </IconButton>
            ) : (
              // Botón de envío normal (cuando no hay agente activo)
              !isRecording && !isProcessingAudio && (
                <Tooltip
                  title={
                    creditBlocked
                      ? "Has agotado el límite de tu plan — pulsa para pedir más uso"
                      : isMessageTooLong
                      ? `Mensaje demasiado largo (${inputValue.length.toLocaleString()} / ${MAX_MESSAGE_LENGTH.toLocaleString()} caracteres)`
                      : isMessageEmpty
                      ? "Escribe un mensaje o adjunta archivos"
                      : isChatGloballyDisabled
                      ? "Esperando respuesta del agente..."
                      : "Enviar mensaje"
                  }
                  arrow
                  placement="top"
                >
                  <span> {/* Wrapper necesario para tooltip con botón disabled */}
                    <IconButton
                      type="submit"
                      sx={{
                        width: { xs: 36, md: 40 },
                        height: { xs: 36, md: 40 },
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        boxShadow: {
                          xs: `0 2px 4px ${alpha(theme.palette.common.black, 0.1)}`,
                          md: `0 2px 8px ${alpha(theme.palette.common.black, 0.15)}`
                        },
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.87),
                          boxShadow: {
                            xs: `0 2px 6px ${alpha(theme.palette.common.black, 0.15)}`,
                            md: `0 4px 12px ${alpha(theme.palette.common.black, 0.2)}`
                          },
                          cursor: 'pointer',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.27),
                          color: theme.palette.primary.contrastText
                        },
                        // Sin crédito se pinta apagado pero sigue vivo: el clic
                        // tiene que llegar a `handleSubmit` para abrir el aviso.
                        ...(creditBlocked && {
                          backgroundColor: alpha(theme.palette.primary.main, 0.27),
                          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.34) },
                        }),
                      }}
                      // `disabled` NATIVO no, cuando falta crédito: el navegador
                      // se traga el click y el usuario no sabría por qué no pasa
                      // nada. `aria-disabled` lo anuncia igual a un lector de
                      // pantalla y deja que se pueda activar para oír el motivo.
                      disabled={creditBlocked ? false : isSendDisabled}
                      aria-disabled={creditBlocked || isSendDisabled}
                      data-testid="chat-send-button"
                    >
                      <KeyboardArrowUpIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )
            )}

            {/* Spinner durante procesamiento de audio */}
            {isProcessingAudio && (
              <CircularProgress size={24} />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

ChatInputInner.displayName = 'ChatInput';

export const ChatInput = React.memo(ChatInputInner);
export default ChatInput;